/**
 * Widget Analytics Events - SINGLE SOURCE OF TRUTH
 *
 * These constants are imported by both:
 * - Main app code (lib/, components/, etc.)
 * - Widget bundle (app/api/embed.js/src/) - bundled at build time via esbuild
 *
 * esbuild inlines these values during the widget build process,
 * so the IIFE bundle remains self-contained.
 */

/**
 * Beacon events sent by the widget via navigator.sendBeacon().
 * Used for pre-iframe analytics and reliable page-exit tracking.
 */
export const BEACON_EVENTS = {
  IFRAME_CREATED: 'widget_iframe_created',
  IFRAME_LOADED: 'widget_iframe_loaded',
  IFRAME_ERROR: 'widget_iframe_error',
  BUTTON_DRAGGED: 'widget_button_dragged',
  OPENED: 'widget_opened',
  CLOSED: 'widget_closed',
  UNLOADED: 'widget_unloaded',
  TOOLTIP_SHOWN: 'widget_tooltip_shown',
  TOOLTIP_CLOSED: 'widget_tooltip_closed',
  ERROR: 'widget_error',
  BROWSER_BLOCKED: 'widget_browser_blocked',
  ORDER_BUTTON_CLICKED: 'order_button_clicked',
  ORDER_CONFIRMED: 'order_confirmed',
  /**
   * The experiment's denominator, and the only event both arms can share: `LOADED` needs a painted
   * launcher, and `LOAD_STARTED` fires from the loader before a variant exists to stamp or
   * `hiddenPages` is known. Once per session — see `docs/experiments.md`.
   */
  EXPERIMENT_ENROLLED: 'widget_experiment_enrolled',
} as const;

/**
 * All widget analytics events including server-side only events.
 *
 * The `dl_*` funnel-bridge family deliberately lives in `datalayer-events.ts`, not
 * here: this module is inlined into EVERY widget bundle, so bridge-only names here
 * ship to all tenants (caught by `bundle-size.test.ts`).
 */
export const WIDGET_ANALYTICS_EVENTS = {
  ...BEACON_EVENTS,

  // Server-side only events (tracked in route handlers, not via beacon)
  /** The loader ran: one per document, sent before we know whether a launcher will appear. Counts
   *  attempts, so it is the denominator to `LOADED`'s numerator. See ANONYMOUS_WIDGET_EVENTS. */
  LOAD_STARTED: 'widget_load_started',
  /** The launcher reached the screen. Not sent when the page hides it, or when the script never
   *  lands, so `uniq(person)` is people who actually saw a widget. */
  LOADED: 'widget_loaded',
  FILE_LOADED: 'widget_file_loaded',
} as const;

/**
 * Captured without a person profile — up to 4x cheaper. Only the *profile* is skipped: PostHog still
 * derives `person_id` from the distinct id, so `uniq(person_id)` and person-level joins keep working,
 * which is what lets a holdout's exposure event sit here. Person *properties* are what these do not
 * set. Verified on production: persons whose only event is an anonymous one all resolve to a row.
 */
const ANONYMOUS_WIDGET_EVENTS: ReadonlySet<string> = new Set([
  WIDGET_ANALYTICS_EVENTS.LOAD_STARTED,
  WIDGET_ANALYTICS_EVENTS.LOADED,
  WIDGET_ANALYTICS_EVENTS.EXPERIMENT_ENROLLED,
]);

export function isAnonymousWidgetEvent(event: string): boolean {
  return ANONYMOUS_WIDGET_EVENTS.has(event);
}

/**
 * Custom window events dispatched by the widget.
 * Host pages can listen for these to react to widget state changes.
 */
export const WINDOW_EVENTS = {
  OPENED: 'bazak-widget-opened',
  CLOSED: 'bazak-widget-closed',
  SET_COMPOSER_TEXT: 'bazak-set-composer-text',
  SET_TRIGGER: 'bazak-set-trigger',
} as const;

/**
 * PostMessage types for iframe communication.
 */
export const MESSAGE_TYPES = {
  WIDGET_EVENT: 'WIDGET_EVENT',
  IFRAME_READY: 'IFRAME_READY',
  PARENT_URL_CHANGED: 'PARENT_URL_CHANGED',
  SUGGESTION_CONTEXT_UPDATED: 'SUGGESTION_CONTEXT_UPDATED',
  NAVIGATE_PARENT: 'NAVIGATE_PARENT',
  OPEN_IN_NEW_TAB: 'OPEN_IN_NEW_TAB',
  PARENT_POINTER_UP: 'PARENT_POINTER_UP',
  PARENT_POINTER_MOVE: 'PARENT_POINTER_MOVE',
  CAROUSEL_DRAG_START: 'CAROUSEL_DRAG_START',
  CAROUSEL_DRAG_END: 'CAROUSEL_DRAG_END',
  LOCALE_CHANGED: 'BAZAK_LOCALE_CHANGED',
  // T19 contact-update (CLIENT strategy): the iframe asks the parent (running on
  // t19.co.il) to read current contact info, then to submit the approved change.
  // The user's t19 session cookies stay in the parent frame — never sent to the iframe/LLM.
  MEITAV_CONTACT_FETCH: 'MEITAV_CONTACT_FETCH',
  MEITAV_CONTACT_FETCH_RESULT: 'MEITAV_CONTACT_FETCH_RESULT',
  MEITAV_CONTACT_SUBMIT: 'MEITAV_CONTACT_SUBMIT',
  MEITAV_CONTACT_SUBMIT_RESULT: 'MEITAV_CONTACT_SUBMIT_RESULT',
  // Generic HITL parent-frame envelope: request `{ type, requestId, toolName, action, payload }`
  // → response `{ type, requestId, ok, payload?, error? }`. Tenant plugins implement the
  // per-tool actions (execution steps, `getCredential`, ...) behind this one wire.
  HITL_TOOL_REQUEST: 'HITL_TOOL_REQUEST',
  HITL_TOOL_RESPONSE: 'HITL_TOOL_RESPONSE',
  // The host page's sign-in state may have changed (logout click, another tab, pill opened);
  // the iframe re-reads its own session. Carries nothing, so a forged one is harmless.
  HOST_AUTH_CHANGED: 'HOST_AUTH_CHANGED',
  // The iframe asks the host page to open its own login. Carries nothing: what to click comes from
  // the served tenant config, so the iframe cannot make the host click anything else.
  OPEN_HOST_LOGIN: 'OPEN_HOST_LOGIN',
  // Sent the moment the host page takes the request, before any loading. Without it the card knows
  // the page cannot open its login (an older script still open in a tab, or no host login there).
  HOST_LOGIN_ACCEPTED: 'HOST_LOGIN_ACCEPTED',
  // The host page's answer: `{ outcome: HostLoginOutcome }`. The card still asks our server.
  HOST_LOGIN_ENDED: 'HOST_LOGIN_ENDED',
} as const;

// localStorage key used by both the iframe and the parent-origin widget
// scripts to persist a manually chosen locale across full page reloads.
// Cookies and sessionStorage are partitioned in cross-site iframes, so the
// parent page's first-party localStorage is the only reliable store.
export const WIDGET_LOCALE_STORAGE_KEY = 'bazak-widget-locale';

// Chat iframe URL param: the widget sets it on a cold open that asked for a new conversation, and
// the chat then skips restoring the last thread, whose async restore would otherwise replace it.
export const NEW_CONVERSATION_PARAM = 'newConversation';

// Chat iframe URL param: the chat opened without the visitor asking (`open({ focus: false })`), so
// nothing in it takes focus until they click or type there.
export const HOLD_FOCUS_PARAM = 'holdFocus';

/**
 * PostMessage actions for widget events.
 */
export const MESSAGE_ACTIONS = {
  OPENED: 'opened',
  CLOSED: 'closed',
  SET_COMPOSER_TEXT: 'setComposerText',
  SET_TRIGGER: 'setTrigger',
} as const;

/**
 * Triggers that can initiate widget open/close actions.
 * Used for analytics to understand how users interact with the widget.
 */
export const WIDGET_TRIGGERS = {
  WIDGET_BUTTON: 'widget-button',
  TOOLTIP: 'tooltip',
  CLOSE_BUTTON: 'close-button',
  OUTSIDE_CLICK: 'outside-click',
  LOADER_CLOSE: 'loader-close',
  API_OPEN: 'api-open',
  API_CLOSE: 'api-close',
  API_TOGGLE: 'api-toggle',
  PAGE_TRIGGER: 'page-trigger',
  HOST_LOGIN: 'host-login',
} as const;

export const BILLING_EVENTS = [BEACON_EVENTS.ORDER_CONFIRMED] as const;

export const BILLING_EVENT_SECRET_NAME = 'billing-event-api-keys';

export type BeaconEvent = (typeof BEACON_EVENTS)[keyof typeof BEACON_EVENTS];
export type WidgetAnalyticsEvent =
  (typeof WIDGET_ANALYTICS_EVENTS)[keyof typeof WIDGET_ANALYTICS_EVENTS];
export type WindowEvent = (typeof WINDOW_EVENTS)[keyof typeof WINDOW_EVENTS];
export type MessageType = (typeof MESSAGE_TYPES)[keyof typeof MESSAGE_TYPES];
export type MessageAction = (typeof MESSAGE_ACTIONS)[keyof typeof MESSAGE_ACTIONS];
export type WidgetTrigger = (typeof WIDGET_TRIGGERS)[keyof typeof WIDGET_TRIGGERS];
